<template>
  <div class="ar-Wrapper">
    <div v-if="!isEmpty(reactions)" class="ar-SelectedReactions">
      <!--
          :disabled="readonly ? true : undefined"
          why this way instead of :disabled="readonly"
          Its because vue-tippy just check is attribute persist but not the value
      -->
      <AppTippy
        v-for="reaction in reactions"
        :key="reaction.reactionCode"
        :append-to="appendTo"
        :disabled="readonly ? true : undefined"
        :on-hide="
          () => {
            currentTooltipInstance.value = null
          }
        "
        :on-show="onTooltipShow"
        :theme="DROP_LIST_THEMES.TRANSLUCENT_NEXT"
        interactive
        placement="top"
      >
        <AppButtonNext
          :class="{
            'ar-Reaction-from-current-user': reaction.fromCurrentUser
          }"
          :disabled="readonly"
          :variant="BUTTON_VARIANTS.TERTIARY_GRAY"
          class="ar-Reaction"
          remove-padding
          @click="handleReactionClick(reaction)"
        >
          {{ decodeEmoji(reaction.reactionCode) }}
          {{ getReactionCount(reaction) }}
        </AppButtonNext>

        <template #content>
          <div class="ar-TooltipContent">
            <p class="ar-TooltipText">
              {{ getTooltipText(reaction) }}
            </p>
            <AppButtonNext
              :variant="BUTTON_VARIANTS.TERTIARY_GRAY"
              class="ar-ShowReactionDetails"
              icon-after="arrow-right-simple-next"
              @click="handleShowDetails(reaction)"
            >
              {{ $t('reactions.show_reaction_details') }}
            </AppButtonNext>
          </div>
        </template>
      </AppTippy>
    </div>

    <div
      v-tippy="{
        content: isReactionLimitExceeded ? $t('reactions.reaction_limit') : '',
        placement: 'top',
        theme: DROP_LIST_THEMES.TRANSLUCENT_NEXT
      }"
    >
      <AppEmojiPicker
        v-if="!readonly"
        ref="emojiPickerRef"
        :append-to="emojiPickerAppendTo"
        :disable="isReactionLimitExceeded"
        @select-emoji="handleAddReactionFromEmojiPicker"
      >
        <template #button="{ isOpen }">
          <AppButtonNext
            :class="{ 'ar-AddReactionBtn-is-open': isOpen }"
            :disable="isReactionLimitExceeded"
            :variant="BUTTON_VARIANTS.TERTIARY_GRAY"
            class="ar-AddReactionBtn"
            icon-before="emoji-category-smileys"
            size="sm"
          >
            {{ $t('reactions.react') }}
          </AppButtonNext>
        </template>
      </AppEmojiPicker>
    </div>

    <portal to="modal-windows">
      <UserReactionsModal
        v-model:show="reactionModalState.isOpen"
        :reactions="reactions"
        :selected-reaction-code="reactionModalState.selectedReactionCode"
        @reaction-select="handleModalReactionSelect"
      />
    </portal>
  </div>
</template>

<script setup>
import { isEmpty } from 'lodash'
import { Portal } from 'portal-vue'
import { computed, ref, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'

import { useReactionsManagement } from '@/composables/reactions-management'
import { getReactionCount } from '@/utils/comments'
import { BUTTON_VARIANTS } from '@/utils/components-configurations/app-button'
import { DROP_LIST_THEMES } from '@/utils/components-configurations/app-droplist'
import { decodeEmoji, encodeEmoji } from '@/utils/emoji'

import AppEmojiPicker from '@/components/AppEmojiPicker'
import UserReactionsModal from '@/components/reactions/UserReactionsModal'
import AppButtonNext from '@/components/ui/AppButtonNext/AppButtonNext'

defineOptions({
  name: 'AppReactions'
})

const props = defineProps({
  commentId: {
    type: [String, Number],
    required: true
  },

  reactions: {
    type: Array,
    default: () => []
  },

  readonly: {
    type: Boolean
  },

  emojiPickerAppendTo: {
    type: String,
    default: ''
  }
})

const emit = defineEmits({
  'update-reactions': null
})

const { t } = useI18n()

const emojiPickerRef = useTemplateRef('emojiPickerRef')

const REACTIONS_LIMIT = 6

const isReactionLimitExceeded = computed(() => {
  return props.reactions.length >= REACTIONS_LIMIT
})

const { reactionModalState, addReaction, removeReaction, openReactionsModal, selectModalReaction } =
  useReactionsManagement(() => props.commentId)

const getTooltipText = reaction => {
  return t('reactions.reaction_by', {
    reaction: decodeEmoji(reaction.reactionCode),
    users: formattedTooltipUsers(reaction)
  })
}

const appendTo = () => document.body

const formattedTooltipUsers = reaction => {
  const users = reaction?.users || []
  if (!users.length) return ''

  const maxVisible = 3

  const visibleUsers = users.slice(0, maxVisible)
  const remainingCount = Math.max(0, users.length - maxVisible)

  const userNames = visibleUsers.map(user => user.userName)
  const withRemaining =
    remainingCount > 0 ? [...userNames, t('reactions.and_others', remainingCount)] : userNames

  return withRemaining.join(', ')
}

const currentTooltipInstance = ref(null)

const onTooltipShow = instance => {
  currentTooltipInstance.value = instance ?? null
}

const handleReactionClick = async reaction => {
  if (props.readonly) return

  if (reaction.fromCurrentUser) {
    const { reactions } = await removeReaction(reaction.reactionCode)
    emit('update-reactions', reactions)
  } else {
    await handleAddReaction(reaction.reactionCode)
  }
}

const handleAddReactionFromEmojiPicker = emoji => {
  if (props.readonly) return

  const reactionCode = encodeEmoji(emoji)
  handleAddReaction(reactionCode)
}

const handleAddReaction = async reactionCode => {
  const { reactions } = await addReaction(reactionCode)

  if (emojiPickerRef.value) {
    await emojiPickerRef.value.togglePicker(false)
  }

  requestAnimationFrame(() => {
    emit('update-reactions', reactions)
  })
}

const handleShowDetails = reaction => {
  currentTooltipInstance.value?.hide()
  openReactionsModal(reaction)
}

const handleModalReactionSelect = reaction => {
  selectModalReaction(reaction)
}
</script>

<style lang="scss" scoped>
.ar-Wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
}

.ar-SelectedReactions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ar-Reaction {
  height: 28px;
  min-width: 40px;
  padding: 0 6px;
  font-size: $fs-14;
  line-height: 20px;
  font-weight: fw('bold');
  display: flex;
  align-items: center;
  justify-content: center;
  color: $dark-2;

  &-from-current-user {
    border: 1px solid var(--gray-blue-100);
    border-radius: $border-radius-sm-next;
  }
}

.ar-AddReactionBtn {
  flex-shrink: 0;
  height: 28px;
}

.ar-TooltipContent {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ar-TooltipText {
  margin: 0;
  font-size: $fs-14;
  line-height: 20px;
}

.ar-ShowReactionDetails {
  background: var(--gray-blue-700);
  font-weight: fw('regular');
  color: var(--white-color);

  &:hover {
    background: var(--gray-blue-800);
  }
}
</style>
